import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const DOMESTIC_EXAM_NAMES = ["WAEC", "NECO", "JAMB UTME", "NABTEB", "Post-UTME"] as const;

export type DomesticExam = { id: string; name: string };
export type Focus = { id: string; exam_id: string; code: string; name: string; sort_order: number };
export type SubjectRow = { id: string; name: string; exam_id: string };
export type FocusPackage = {
  id: string; exam_id: string; focus_id: string;
  title: string; description: string | null;
  price_naira: number; currency: string; active: boolean;
};
export type CbtPlan = {
  id: string; code: string; name: string;
  price_naira: number; duration_days: number; active: boolean; sort_order: number;
};
export type StudentPrefs = {
  user_id: string; exam_id: string; focus_id: string | null;
  subject_ids: string[]; created_at: string; updated_at: string;
};
export type Entitlement = {
  id: string; user_id: string; package_id: string | null; intl_package_id: string | null;
  source: string; expires_at: string | null; created_at: string; is_archived: boolean;
};
export type CbtSubscription = {
  id: string; user_id: string; plan_id: string;
  started_at: string; expires_at: string; status: string; source: string;
  months_purchased: number; payment_reference: string | null;
};

export function useDomesticExams() {
  return useQuery({
    queryKey: ["domestic-exams"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exams")
        .select("id,name")
        .eq("is_international", false)
        .in("name", DOMESTIC_EXAM_NAMES as unknown as string[])
        .order("name");
      if (error) throw error;
      return (data ?? []) as DomesticExam[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useFocuses(examId?: string) {
  return useQuery({
    queryKey: ["exam-focuses", examId],
    enabled: !!examId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_focuses")
        .select("id,exam_id,code,name,sort_order")
        .eq("exam_id", examId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as Focus[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useExamSubjects(examId?: string) {
  return useQuery({
    queryKey: ["exam-subjects", examId],
    enabled: !!examId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects")
        .select("id,name,exam_id")
        .eq("exam_id", examId!)
        .order("name");
      if (error) throw error;
      return (data ?? []) as SubjectRow[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useRecommendedSubjects(focusId?: string) {
  return useQuery({
    queryKey: ["focus-recommended", focusId],
    enabled: !!focusId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("focus_recommended_subjects")
        .select("subject_id, subjects(id,name,exam_id)")
        .eq("focus_id", focusId!);
      if (error) throw error;
      return (data ?? []).map((r) => (r as { subjects: SubjectRow }).subjects).filter(Boolean);
    },
    staleTime: 5 * 60_000,
  });
}

export function useFocusPackages() {
  return useQuery({
    queryKey: ["focus-packages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("focus_packages")
        .select("*")
        .eq("active", true)
        .eq("is_archived", false);
      if (error) throw error;
      return (data ?? []) as FocusPackage[];
    },
    staleTime: 60_000,
  });
}

export function useCbtPlans() {
  return useQuery({
    queryKey: ["cbt-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cbt_plans")
        .select("*")
        .eq("active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as CbtPlan[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useMyPrefs() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my-prefs", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<StudentPrefs | null> => {
      const { data, error } = await supabase
        .from("student_exam_prefs")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data as StudentPrefs | null) ?? null;
    },
  });
}

export function useSavePrefs() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { exam_id: string; focus_id: string | null; subject_ids: string[] }) => {
      const { error } = await supabase
        .from("student_exam_prefs")
        .upsert({ user_id: user!.id, ...payload }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-prefs"] }),
  });
}

export function useMyEntitlements(opts?: { includeArchived?: boolean }) {
  const { user } = useAuth();
  const includeArchived = !!opts?.includeArchived;
  return useQuery({
    queryKey: ["my-entitlements", user?.id, includeArchived],
    enabled: !!user,
    queryFn: async () => {
      let q = supabase.from("student_entitlements").select("*").eq("user_id", user!.id);
      if (!includeArchived) q = q.eq("is_archived", false);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Entitlement[];
    },
  });
}

export function useSetEntitlementArchived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from("student_entitlements")
        .update({ is_archived: archived })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-entitlements"] });
      qc.invalidateQueries({ queryKey: ["my-intl-ent"] });
    },
  });
}

export function useMyCbt(opts?: { includeExpired?: boolean }) {
  const { user } = useAuth();
  const includeExpired = !!opts?.includeExpired;
  return useQuery({
    queryKey: ["my-cbt", user?.id, includeExpired],
    enabled: !!user,
    queryFn: async () => {
      let query = supabase
        .from("cbt_subscriptions")
        .select("*")
        .eq("user_id", user!.id);
      if (!includeExpired) query = query.eq("status", "active");
      query = query.order("expires_at", { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      const rows = (data ?? []) as CbtSubscription[];
      if (includeExpired) return rows[0] ?? null;
      return rows.find((r) => new Date(r.expires_at).getTime() > Date.now()) ?? null;
    },
  });
}

export function packageFor(exams: DomesticExam[], focuses: Focus[], packages: FocusPackage[], examId?: string, focusId?: string | null) {
  if (!examId || !focusId) return null;
  return packages.find((p) => p.exam_id === examId && p.focus_id === focusId) ?? null;
}
